// === Claude Studio - Types, API & Utilities ===

// --- Types ---

export interface ProcessDescription {
  problem?: string;
  scope?: string;
  rollen?: Array<{ name: string; verantwortung: string }>;
  prozesse?: Array<{
    name: string;
    ziel: string;
    eingaben?: string[];
    ausgaben?: string[];
    regeln?: string[];
    risiken?: string[];
  }>;
  akzeptanzkriterien?: string[];
}

export interface ArtifactData {
  mermaid?: string;
  beschreibung?: ProcessDescription;
  hinweise?: string[];
  html?: string;
  viewport?: 'desktop' | 'tablet' | 'mobile';
  content?: string;
  documentType?: string;
  description?: string;
}

export interface Artifact {
  id: string;
  type: 'process' | 'wireframe' | 'document';
  title: string;
  createdAt: string;
  updatedAt: string;
  data: ArtifactData;
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  artifacts: { process: number; wireframe: number; document: number };
  totalArtifacts: number;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  artifacts: Artifact[];
}

export type ArtifactType = 'process' | 'wireframe' | 'document';

export interface NewArtifact {
  type: ArtifactType;
  title: string;
  data: ArtifactData;
}

export interface ArtifactUpdate {
  title?: string;
  data?: Partial<ArtifactData>;
}

export interface HealthStatus {
  claude: boolean;
  status: string;
}

// --- API ---

const API = '/api/studio';

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Anfrage fehlgeschlagen');
  }
  return res.json();
}

export const studioApi = {
  listProjects: () => api<{ projects: ProjectSummary[] }>('/projects').then(d => d.projects),
  getProject: (id: string) => api<Project>(`/projects/${id}`),
  createProject: (name: string, description: string) =>
    api<Project>('/projects', { method: 'POST', body: JSON.stringify({ name, description }) }),
  updateProject: (id: string, updates: { name?: string; description?: string }) =>
    api<Project>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  deleteProject: (id: string) =>
    api<{ success: boolean }>(`/projects/${id}`, { method: 'DELETE' }),
  addArtifact: (projectId: string, artifact: NewArtifact) =>
    api<{ artifactId: string; artifact: Artifact }>(`/projects/${projectId}/artifacts`, {
      method: 'POST', body: JSON.stringify(artifact),
    }),
  updateArtifact: (projectId: string, artifactId: string, updates: ArtifactUpdate) =>
    api<{ success: boolean; artifact: Artifact }>(`/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'PUT', body: JSON.stringify(updates),
    }),
  deleteArtifact: (projectId: string, artifactId: string) =>
    api<{ success: boolean }>(`/projects/${projectId}/artifacts/${artifactId}`, { method: 'DELETE' }),
  exportProject: (id: string) => api<Project>(`/projects/${id}/export`),
  importProject: (data: unknown) =>
    api<Project>('/projects/import', { method: 'POST', body: JSON.stringify(data) }),
  health: () => api<HealthStatus>('/health').catch(() => ({ claude: false, status: 'offline' })),
};

// --- Formatting Utilities ---

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const TYPE_LABELS: Record<string, string> = {
  process: 'Prozess', wireframe: 'Wireframe', document: 'Dokument',
};
const TYPE_LABELS_PLURAL: Record<string, string> = {
  process: 'Prozesse', wireframe: 'Wireframes', document: 'Dokumente',
};
const DOC_TYPE_LABELS: Record<string, string> = {
  requirements: 'Anforderungen', specification: 'Spezifikation',
  'user-story': 'User Story', architecture: 'Architektur',
  'meeting-notes': 'Meeting-Notizen', other: 'Sonstiges',
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] || type;
}

export function typeLabelPlural(type: string): string {
  return TYPE_LABELS_PLURAL[type] || type;
}

export function docTypeLabel(dt: string): string {
  return DOC_TYPE_LABELS[dt] || dt;
}

// --- Markdown Rendering (XSS-safe) ---

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderTable(tableStr: string): string {
  const lines = tableStr.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return escapeHtml(tableStr);

  const parseRow = (line: string) =>
    line.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length);

  const headerCells = parseRow(lines[0]);
  if (headerCells.length === 0) return escapeHtml(tableStr);

  // Check if second line is a separator
  const sep = lines[1];
  if (!/^[\s|:\-]+$/.test(sep)) return escapeHtml(tableStr);

  let html = '<table class="studio-table"><thead><tr>';
  for (const cell of headerCells) {
    html += `<th>${cell}</th>`;
  }
  html += '</tr></thead><tbody>';

  for (let i = 2; i < lines.length; i++) {
    const cells = parseRow(lines[i]);
    html += '<tr>';
    for (let j = 0; j < headerCells.length; j++) {
      html += `<td>${cells[j] || ''}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  return html;
}

export function renderMarkdown(md: string): string {
  if (!md) return '';

  // Escape HTML to prevent XSS
  let html = escapeHtml(md);

  // Code blocks (before other transforms, content stays escaped)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
    const cls = lang ? ` data-lang="${lang}"` : '';
    return `<pre class="studio-code-block"${cls}><code>${code}</code></pre>`;
  });

  // Tables (detect | header | \n |---| \n | row | patterns)
  html = html.replace(/((?:^|\n)\|[^\n]+\|\n\|[\s|:\-]+\|\n(?:\|[^\n]+\|\n?)+)/g, (_m, table) => {
    return renderTable(table);
  });

  // Headers
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Inline formatting
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/`(.+?)`/g, '<code class="studio-inline-code">$1</code>');

  // Lists
  html = html.replace(/^\- \[x\] (.+)$/gm, '<li class="studio-checked">&#9745; $1</li>');
  html = html.replace(/^\- \[ \] (.+)$/gm, '<li class="studio-unchecked">&#9744; $1</li>');
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr/>');

  // Links (only allow safe protocols)
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, (_m, text, url) => {
    if (/^(https?:|mailto:|#)/i.test(url)) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    }
    return text;
  });

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br/>');

  // Wrap lists
  html = html.replace(/((?:<li[^>]*>.*?<\/li>(?:<br\/>)?)+)/g, '<ul>$1</ul>');

  return `<p>${html}</p>`;
}

// --- Export Helpers ---

export function downloadSVG(svgElement: SVGSVGElement | null, filename: string) {
  if (!svgElement) return;
  const data = new XMLSerializer().serializeToString(svgElement);
  const blob = new Blob([data], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename.replace(/[^a-z0-9]/gi, '_')}.svg`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadPNG(svgElement: SVGSVGElement | null, filename: string) {
  if (!svgElement) return;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const vb = svgElement.viewBox.baseVal;
  const w = vb.width || svgElement.width.baseVal.value || 800;
  const h = vb.height || svgElement.height.baseVal.value || 600;
  const scale = 2;
  canvas.width = w * scale;
  canvas.height = h * scale;
  const data = new XMLSerializer().serializeToString(svgElement);
  const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((pngBlob) => {
      if (!pngBlob) { URL.revokeObjectURL(url); return; }
      const pngUrl = URL.createObjectURL(pngBlob);
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `${filename.replace(/[^a-z0-9]/gi, '_')}.png`;
      a.click();
      URL.revokeObjectURL(pngUrl);
    }, 'image/png');
    URL.revokeObjectURL(url);
  };
  img.onerror = () => URL.revokeObjectURL(url);
  img.src = url;
}
